// src/components/Product/ProductEditForm.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createProductFD, updateProductFD } from "../../services/productService";

function resolveProductCategoryId(p) {
  if (!p) return "";
  return p.categoryId ?? p.category_id ?? p.Category?.id ?? "";
}

// побудова шляхів лише для "листків": "Меблі → Столи → Журнальні"
function buildLeafOptions(categories) {
  const out = [];
  const walk = (nodes, path = []) => {
    for (const n of nodes || []) {
      const nextPath = [...path, n.name];
      const hasChildren = Array.isArray(n.subcategories) && n.subcategories.length > 0;
      if (hasChildren) {
        walk(n.subcategories, nextPath);
      } else {
        out.push({ id: n.id, name: nextPath.join(" → ") });
      }
    }
  };
  walk(categories || []);
  out.sort((a, b) => a.name.localeCompare(b.name, "uk"));
  return out;
}

export default function ProductEditForm({ product, categories, onSaved, onCancel }) {
  const isEdit = !!product?.id;

  // ====== UA поля ======
  const [name, setName] = useState(product?.name || "");
  const [price, setPrice] = useState(product?.price ?? "");
  const [description, setDescription] = useState(product?.description || "");
  const [size, setSize] = useState(product?.size || ""); // 🟢 текстове поле "розмір"
  const [categoryId, setCategoryId] = useState(resolveProductCategoryId(product));

  // ====== EN поля ======
  const [nameEn, setNameEn] = useState(product?.name_en || "");
  const [priceEn, setPriceEn] = useState(
    product?.price_en !== undefined && product?.price_en !== null ? product.price_en : ""
  );
  const [descriptionEn, setDescriptionEn] = useState(product?.description_en || "");
  const [sizeEn, setSizeEn] = useState(product?.size_en || "");

  // головне зображення (нове)
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);

  // існуюче головне фото (URL з бекенду)
  const [existingMainUrl, setExistingMainUrl] = useState(product?.mainImageUrl || null);

  // існуючі додаткові (public_id + url)
  const [existingExtra, setExistingExtra] = useState(() => {
    const pids = Array.isArray(product?.extraImages) ? product.extraImages : [];
    const urls = Array.isArray(product?.extraImageUrls) ? product.extraImageUrls : [];
    return pids.map((pid, i) => ({ pid, url: urls[i] || null, keep: true }));
  });

  // нові додаткові
  const [newExtraFiles, setNewExtraFiles] = useState([]);
  const [newExtraPreviews, setNewExtraPreviews] = useState([]);

  const dropRef = useRef(null);

  // коли product зміниться — оновити поля
  useEffect(() => {
    // UA
    setName(product?.name || "");
    setPrice(product?.price ?? "");
    setDescription(product?.description || "");
    setSize(product?.size || "");
    setCategoryId(resolveProductCategoryId(product));
    // EN
    setNameEn(product?.name_en || "");
    setPriceEn(
      product?.price_en !== undefined && product?.price_en !== null ? product.price_en : ""
    );
    setDescriptionEn(product?.description_en || "");
    setSizeEn(product?.size_en || "");

    // images
    setExistingMainUrl(product?.mainImageUrl || null);

    const pids = Array.isArray(product?.extraImages) ? product.extraImages : [];
    const urls = Array.isArray(product?.extraImageUrls) ? product.extraImageUrls : [];
    setExistingExtra(pids.map((pid, i) => ({ pid, url: urls[i] || null, keep: true })));

    setMainImageFile(null);
    setMainImagePreview(null);
    setNewExtraFiles([]);
    setNewExtraPreviews([]);
  }, [product]);

  // прів’ю головного фото
  useEffect(() => {
    if (!mainImageFile) {
      setMainImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(mainImageFile);
    setMainImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [mainImageFile]);

  // прів’ю нових extra
  useEffect(() => {
    newExtraPreviews.forEach((u) => URL.revokeObjectURL(u));
    const urls = newExtraFiles.map((f) => URL.createObjectURL(f));
    setNewExtraPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newExtraFiles]);

  // тільки leaf-категорії у селекті
  const leafOptions = useMemo(() => buildLeafOptions(categories), [categories]);

  // drag&drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDrop = (e) => {
      prevent(e);
      const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
      if (files.length) setNewExtraFiles((prev) => [...prev, ...files]);
    };
    ["dragenter", "dragover", "dragleave", "drop"].forEach((type) => el.addEventListener(type, prevent));
    el.addEventListener("drop", onDrop);
    return () => {
      ["dragenter", "dragover", "dragleave", "drop"].forEach((type) => el.removeEventListener(type, prevent));
      el.removeEventListener("drop", onDrop);
    };
  }, []);

  const toggleKeep = (pid) => {
    setExistingExtra((prev) =>
      prev.map((x) => (x.pid === pid ? { ...x, keep: !x.keep } : x))
    );
  };

  const removeNewExtra = (idx) => {
    setNewExtraFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onPickNewExtra = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length) setNewExtraFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };
  const onPickMainImage = (e) => {
    const f = e.target.files?.[0] || null;
    setMainImageFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Вкажіть назву");
    if (!categoryId) return alert("Оберіть категорію (листок)");

    try {
      const fd = new FormData();

      // ===== UA поля =====
      fd.append("name", name.trim());
      if (description) fd.append("description", description);
      if (size) fd.append("size", size);
      if (price !== "" && price !== null) fd.append("price", String(price));

      // ===== EN поля =====
      if (nameEn) fd.append("name_en", nameEn.trim());
      if (descriptionEn) fd.append("description_en", descriptionEn);
      if (sizeEn) fd.append("size_en", sizeEn);
      // Бекенд приймає і price_en, і priceEn — відправляємо price_en
      if (priceEn !== "" && priceEn !== null) fd.append("price_en", String(priceEn));

      fd.append("categoryId", String(categoryId));

      // Фото
      if (mainImageFile) fd.append("mainImage", mainImageFile);

      // Які extra залишити в поточному порядку
      const keepPids = existingExtra.filter((x) => x.keep).map((x) => x.pid);
      fd.append("extraImagesKeep", JSON.stringify(keepPids));

      // Нові extra
      newExtraFiles.forEach((file) => fd.append("images", file));

      if (isEdit) await updateProductFD(product.id, fd);
      else await createProductFD(fd);

      onSaved?.();
    } catch (err) {
      console.error("Помилка збереження товару:", err);
      alert("Не вдалося зберегти товар");
    }
  };

  // Допоміжний копіювач з UA → EN (за бажанням заповнити швидко)
  const copyUaToEn = () => {
    if (!nameEn) setNameEn(name);
    if (!descriptionEn) setDescriptionEn(description);
    if (!sizeEn) setSizeEn(size);
    // ціну англ. копіювати не будемо, валюта може відрізнятися
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* ======= Українська ======= */}
      <div className="rounded-lg border p-4">
        <h3 className="font-semibold mb-3">Українською</h3>

        {/* Назва */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Назва</label>
          <input
            className="border rounded w-full p-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Напр., Журнальний столик"
            required
          />
        </div>

        {/* Ціна (₴) */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Ціна (₴)</label>
          <input
            type="number"
            className="border rounded w-full p-2"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            min="0"
            step="0.01"
            placeholder="Напр., 2500"
          />
        </div>

        {/* Опис */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Опис</label>
          <textarea
            className="border rounded w-full p-2 min-h-[100px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Короткий опис виробу…"
          />
        </div>

        {/* Розмір */}
        <div>
          <label className="block text-sm font-medium mb-1">Розмір</label>
          <input
            className="border rounded w-full p-2"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            placeholder="Напр., 120×60×75 см"
          />
        </div>
      </div>

      {/* ======= Англійською ======= */}
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">English</h3>
          <button
            type="button"
            className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
            onClick={copyUaToEn}
            title="Скопіювати з української"
          >
            Заповнити з UA
          </button>
        </div>

        {/* Name (EN) */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Name (EN)</label>
          <input
            className="border rounded w-full p-2"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder="e.g., Coffee table"
          />
        </div>

        {/* Price (EN, $) */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Price (USD, $)</label>
          <input
            type="number"
            className="border rounded w-full p-2"
            value={priceEn}
            onChange={(e) => setPriceEn(e.target.value)}
            min="0"
            
            placeholder="e.g., 550"
          />
        </div>

        {/* Description (EN) */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description (EN)</label>
          <textarea
            className="border rounded w-full p-2 min-h-[100px]"
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
            placeholder="Short description in English…"
          />
        </div>

        {/* Size (EN) */}
        <div>
          <label className="block text-sm font-medium mb-1">Size (EN)</label>
          <input
            className="border rounded w-full p-2"
            value={sizeEn}
            onChange={(e) => setSizeEn(e.target.value)}
            placeholder="e.g., 120×60×75 cm"
          />
        </div>
      </div>

      {/* Категорія */}
      <div>
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium mb-1">Категорія</label>
          <span className="text-xs text-gray-500">* Доступні лише кінцеві підкатегорії</span>
        </div>
        <select
          className="border rounded w-full p-2"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          required
        >
          <option value="">— Оберіть кінцеву підкатегорію —</option>
          {leafOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Головне фото */}
      <div>
        <label className="block text-sm font-medium mb-1">Головне фото</label>
        <div className="flex items-center gap-4">
          <input type="file" accept="image/*" onChange={onPickMainImage} />
          {(mainImagePreview || existingMainUrl) && (
            <img
              src={mainImagePreview || existingMainUrl}
              alt="main preview"
              className="h-16 w-16 object-cover rounded border"
            />
          )}
        </div>
      </div>

      {/* Існуючі додаткові фото */}
      {existingExtra.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">Існуючі додаткові фото</label>
          <div className="flex flex-wrap gap-3">
            {existingExtra.map((img) => (
              <div key={img.pid} className="relative">
                <div className="h-20 w-20 rounded border overflow-hidden bg-[#ebe1d5ff] flex items-center justify-center">
                  {img.url ? (
                    <img src={img.url} alt={img.pid} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] p-1 break-all text-gray-600">{img.pid}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleKeep(img.pid)}
                  className={`absolute -top-2 -right-2 text-xs px-2 py-1 rounded ${
                    img.keep ? "bg-red-500 text-white" : "bg-green-600 text-white"
                  }`}
                  title={img.keep ? "Не залишати (видалити)" : "Залишити"}
                >
                  {img.keep ? "×" : "↩"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Нові додаткові фото */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Додати фото (drag & drop або вибір файлів)
        </label>

        <div
          ref={dropRef}
          className="border-2 border-dashed rounded p-6 text-center hover:bg-gray-50"
        >
          Перетягніть зображення сюди
          <div className="mt-3">
            <input type="file" accept="image/*" multiple onChange={onPickNewExtra} />
          </div>
        </div>

        {newExtraFiles.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3">
            {newExtraFiles.map((file, idx) => (
              <div key={`${file.name}-${idx}`} className="relative">
                <img
                  src={newExtraPreviews[idx]}
                  alt={file.name}
                  className="h-20 w-20 object-cover rounded border"
                />
                <button
                  type="button"
                  onClick={() => removeNewExtra(idx)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded"
                  title="Прибрати"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Дії */}
      <div className="flex gap-3">
        <button
          type="submit"
          className="bg-[#4B2E2B] text-white px-5 py-2 rounded hover:bg-[#3A2422]"
        >
          {isEdit ? "Оновити" : "Створити"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="border px-5 py-2 rounded hover:bg-gray-100">
            Скасувати
          </button>
        )}
      </div>
    </form>
  );
}

ProductEditForm.propTypes = {
  product: PropTypes.object,
  categories: PropTypes.array,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
};


// // src/components/Product/ProductEditForm.js
// import React, { useEffect, useMemo, useRef, useState } from "react";
// import PropTypes from "prop-types";
// import { createProductFD, updateProductFD } from "../../services/productService";

// function resolveProductCategoryId(p) {
//   if (!p) return "";
//   return p.categoryId ?? p.category_id ?? p.Category?.id ?? "";
// }

// // побудова шляхів лише для "листків": "Меблі → Столи → Журнальні"
// function buildLeafOptions(categories) {
//   const out = [];
//   const walk = (nodes, path = []) => {
//     for (const n of nodes || []) {
//       const nextPath = [...path, n.name];
//       const hasChildren = Array.isArray(n.subcategories) && n.subcategories.length > 0;
//       if (hasChildren) {
//         walk(n.subcategories, nextPath);
//       } else {
//         out.push({ id: n.id, name: nextPath.join(" → ") });
//       }
//     }
//   };
//   walk(categories || []);
//   out.sort((a, b) => a.name.localeCompare(b.name, "uk"));
//   return out;
// }

// export default function ProductEditForm({ product, categories, onSaved, onCancel }) {
//   const isEdit = !!product?.id;

//   // поля
//   const [name, setName] = useState(product?.name || "");
//   const [price, setPrice] = useState(product?.price ?? "");
//   const [description, setDescription] = useState(product?.description || "");
//   const [size, setSize] = useState(product?.size || ""); // 🟢 нове поле
//   const [categoryId, setCategoryId] = useState(resolveProductCategoryId(product));

//   // головне зображення (нове)
//   const [mainImageFile, setMainImageFile] = useState(null);
//   const [mainImagePreview, setMainImagePreview] = useState(null);

//   // існуюче головне фото (URL з бекенду)
//   const [existingMainUrl, setExistingMainUrl] = useState(product?.mainImageUrl || null);

//   // існуючі додаткові (public_id + url)
//   const [existingExtra, setExistingExtra] = useState(() => {
//     const pids = Array.isArray(product?.extraImages) ? product.extraImages : [];
//     const urls = Array.isArray(product?.extraImageUrls) ? product.extraImageUrls : [];
//     return pids.map((pid, i) => ({ pid, url: urls[i] || null, keep: true }));
//   });

//   // нові додаткові
//   const [newExtraFiles, setNewExtraFiles] = useState([]);
//   const [newExtraPreviews, setNewExtraPreviews] = useState([]);

//   const dropRef = useRef(null);

//   // коли product зміниться — оновити поля
//   useEffect(() => {
//     setName(product?.name || "");
//     setPrice(product?.price ?? "");
//     setDescription(product?.description || "");
//     setSize(product?.size || ""); // 🟢 нове поле
//     setCategoryId(resolveProductCategoryId(product));

//     setExistingMainUrl(product?.mainImageUrl || null);

//     const pids = Array.isArray(product?.extraImages) ? product.extraImages : [];
//     const urls = Array.isArray(product?.extraImageUrls) ? product.extraImageUrls : [];
//     setExistingExtra(pids.map((pid, i) => ({ pid, url: urls[i] || null, keep: true })));

//     setMainImageFile(null);
//     setMainImagePreview(null);
//     setNewExtraFiles([]);
//     setNewExtraPreviews([]);
//   }, [product]);

//   // прів’ю головного фото
//   useEffect(() => {
//     if (!mainImageFile) {
//       setMainImagePreview(null);
//       return;
//     }
//     const url = URL.createObjectURL(mainImageFile);
//     setMainImagePreview(url);
//     return () => URL.revokeObjectURL(url);
//   }, [mainImageFile]);

//   // прів’ю нових extra
//   useEffect(() => {
//     newExtraPreviews.forEach((u) => URL.revokeObjectURL(u));
//     const urls = newExtraFiles.map((f) => URL.createObjectURL(f));
//     setNewExtraPreviews(urls);
//     return () => urls.forEach((u) => URL.revokeObjectURL(u));
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [newExtraFiles]);

//   // тільки leaf-категорії у селекті
//   const leafOptions = useMemo(() => buildLeafOptions(categories), [categories]);

//   // drag&drop
//   useEffect(() => {
//     const el = dropRef.current;
//     if (!el) return;
//     const prevent = (e) => {
//       e.preventDefault();
//       e.stopPropagation();
//     };
//     const onDrop = (e) => {
//       prevent(e);
//       const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
//       if (files.length) setNewExtraFiles((prev) => [...prev, ...files]);
//     };
//     ["dragenter", "dragover", "dragleave", "drop"].forEach((type) => el.addEventListener(type, prevent));
//     el.addEventListener("drop", onDrop);
//     return () => {
//       ["dragenter", "dragover", "dragleave", "drop"].forEach((type) => el.removeEventListener(type, prevent));
//       el.removeEventListener("drop", onDrop);
//     };
//   }, []);

//   const toggleKeep = (pid) => {
//     setExistingExtra((prev) =>
//       prev.map((x) => (x.pid === pid ? { ...x, keep: !x.keep } : x))
//     );
//   };

//   const removeNewExtra = (idx) => {
//     setNewExtraFiles((prev) => prev.filter((_, i) => i !== idx));
//   };

//   const onPickNewExtra = (e) => {
//     const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
//     if (files.length) setNewExtraFiles((prev) => [...prev, ...files]);
//     e.target.value = "";
//   };
//   const onPickMainImage = (e) => {
//     const f = e.target.files?.[0] || null;
//     setMainImageFile(f);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!name.trim()) return alert("Вкажіть назву");
//     if (!categoryId) return alert("Оберіть категорію (листок)");

//     try {
//       const fd = new FormData();
//       fd.append("name", name.trim());
//       if (description) fd.append("description", description);
//       if (size) fd.append("size", size); // 🟢 нове поле
//       if (price !== "" && price !== null) fd.append("price", String(price));
//       fd.append("categoryId", String(categoryId));

//       if (mainImageFile) fd.append("mainImage", mainImageFile);

//       const keepPids = existingExtra.filter((x) => x.keep).map((x) => x.pid);
//       fd.append("extraImagesKeep", JSON.stringify(keepPids));

//       newExtraFiles.forEach((file) => fd.append("images", file));

//       if (isEdit) await updateProductFD(product.id, fd);
//       else await createProductFD(fd);

//       onSaved?.();
//     } catch (err) {
//       console.error("Помилка збереження товару:", err);
//       alert("Не вдалося зберегти товар");
//     }
//   };

//   return (
//     <form onSubmit={handleSubmit} className="space-y-6">

//       {/* Назва */}
//       <div>
//         <label className="block text-sm font-medium mb-1">Назва</label>
//         <input
//           className="border rounded w-full p-2"
//           value={name}
//           onChange={(e) => setName(e.target.value)}
//           placeholder="Напр., Журнальний столик"
//           required
//         />
//       </div>

//       {/* Категорія */}
//       <div>
//         <div className="flex items-center justify-between">
//           <label className="block text-sm font-medium mb-1">Категорія</label>
//           <span className="text-xs text-gray-500">* Доступні лише кінцеві підкатегорії</span>
//         </div>
//         <select
//           className="border rounded w-full p-2"
//           value={categoryId}
//           onChange={(e) => setCategoryId(e.target.value)}
//           required
//         >
//           <option value="">— Оберіть кінцеву підкатегорію —</option>
//           {leafOptions.map((c) => (
//             <option key={c.id} value={c.id}>
//               {c.name}
//             </option>
//           ))}
//         </select>
//       </div>

//       {/* Ціна */}
//       <div>
//         <label className="block text-sm font-medium mb-1">Ціна (₴)</label>
//         <input
//           type="number"
//           className="border rounded w-full p-2"
//           value={price}
//           onChange={(e) => setPrice(e.target.value)}
//           min="0"
//           step="0.01"
//           placeholder="Напр., 2500"
//         />
//       </div>

//       {/* Опис */}
//       <div>
//         <label className="block text-sm font-medium mb-1">Опис</label>
//         <textarea
//           className="border rounded w-full p-2 min-h-[100px]"
//           value={description}
//           onChange={(e) => setDescription(e.target.value)}
//           placeholder="Короткий опис виробу…"
//         />
//       </div>

//       {/* 🟢 Розмір */}
//       <div>
//         <label className="block text-sm font-medium mb-1">Розмір</label>
//         <input
//           className="border rounded w-full p-2"
//           value={size}
//           onChange={(e) => setSize(e.target.value)}
//           placeholder="Напр., 120×60×75 см"
//         />
//       </div>

//       {/* решта — без змін */}
//       {/* ... далі йде блоки з фото, extraImages і кнопками ... */}
//       {/* (не скорочував, щоб не загубити твою структуру) */}
//       {/* повністю те саме, що в тебе було */}

//       {/* Головне фото */}
//       <div>
//         <label className="block text-sm font-medium mb-1">Головне фото</label>
//         <div className="flex items-center gap-4">
//           <input type="file" accept="image/*" onChange={onPickMainImage} />
//           {(mainImagePreview || existingMainUrl) && (
//             <img
//               src={mainImagePreview || existingMainUrl}
//               alt="main preview"
//               className="h-16 w-16 object-cover rounded border"
//             />
//           )}
//         </div>
//       </div>
//  {/* Існуючі додаткові фото */}
//         {existingExtra.length > 0 && (
//         <div>
//           <label className="block text-sm font-medium mb-2">Існуючі додаткові фото</label>
//           <div className="flex flex-wrap gap-3">
//             {existingExtra.map((img) => (
//               <div key={img.pid} className="relative">
//                 <div className="h-20 w-20 rounded border overflow-hidden bg-[#ebe1d5ff] flex items-center justify-center">
//                   {img.url ? (
//                     <img src={img.url} alt={img.pid} className="w-full h-full object-cover" />
//                   ) : (
//                     <span className="text-[10px] p-1 break-all text-gray-600">{img.pid}</span>
//                   )}
//                 </div>
//                 <button
//                   type="button"
//                   onClick={() => toggleKeep(img.pid)}
//                   className={`absolute -top-2 -right-2 text-xs px-2 py-1 rounded ${
//                     img.keep ? "bg-red-500 text-white" : "bg-green-600 text-white"
//                   }`}
//                   title={img.keep ? "Не залишати (видалити)" : "Залишити"}
//                 >
//                   {img.keep ? "×" : "↩"}
//                 </button>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Нові додаткові фото */}
//       <div>
//         <label className="block text-sm font-medium mb-2">
//           Додати фото (drag & drop або вибір файлів)
//         </label>

//         <div
//           ref={dropRef}
//           className="border-2 border-dashed rounded p-6 text-center hover:bg-gray-50"
//         >
//           Перетягніть зображення сюди
//           <div className="mt-3">
//             <input type="file" accept="image/*" multiple onChange={onPickNewExtra} />
//           </div>
//         </div>

//         {newExtraFiles.length > 0 && (
//           <div className="mt-3 flex flex-wrap gap-3">
//             {newExtraFiles.map((file, idx) => (
//               <div key={`${file.name}-${idx}`} className="relative">
//                 <img
//                   src={newExtraPreviews[idx]}
//                   alt={file.name}
//                   className="h-20 w-20 object-cover rounded border"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => removeNewExtra(idx)}
//                   className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded"
//                   title="Прибрати"
//                 >
//                   ×
//                 </button>
//               </div>
//             ))}
//           </div>
//         )}
//       </div> 
//       {/* ... (інші блоки без змін) ... */}

//       {/* Дії */}
//       <div className="flex gap-3">
//         <button
//           type="submit"
//           className="bg-[#4B2E2B] text-white px-5 py-2 rounded hover:bg-[#3A2422]"
//         >
//           {isEdit ? "Оновити" : "Створити"}
//         </button>
//         {onCancel && (
//           <button type="button" onClick={onCancel} className="border px-5 py-2 rounded hover:bg-gray-100">
//             Скасувати
//           </button>
//         )}
//       </div>
//     </form>
//   );
// }

// ProductEditForm.propTypes = {
//   product: PropTypes.object,
//   categories: PropTypes.array,
//   onSaved: PropTypes.func,
//   onCancel: PropTypes.func,
// };


